/**
 * Decision logic for resuming a build that was interrupted (page refresh / lost tab) after
 * its files were snapshotted but before the build was verified.
 *
 * Kept dependency-free so it stays unit-testable in the node test env.
 */

/**
 * - `failed`: the local build ran and reported code errors (tsc/vite).
 * - `environment_failure`: the build could not run in this browser (WebContainer never became
 *   ready, template setup failed, an unexpected exception). Never a reason to spend credits.
 * - `fix_handled`: the local build failed and the versions API decided what follows (an
 *   automatic fix stream or the "needs your input" card, P6.4b), so callers start nothing.
 */
export type ResumeBuildOutcome =
  | 'success'
  | 'failed'
  | 'fix_handled'
  /** P6.4d-3: the builder had no files for this draft's turn; show the retry state. */
  | 'files_unavailable'
  | 'environment_failure'
  | 'skipped';

export type ResumeBuildAction = 'resume' | 'defer' | 'skip';

export function decideResumeBuildAction(args: {
  hasSnapshotEntry: boolean;
  buildVerified: boolean;
  bootstrap: 'pending' | 'restored' | 'none' | 'failed';
  isStreaming: boolean;
  clientToolsBusy: boolean;
  inFlight: boolean;
  alreadyAttempted: boolean;
}): ResumeBuildAction {
  if (!args.hasSnapshotEntry) return 'skip';
  if (args.buildVerified) return 'skip';
  if (args.inFlight || args.alreadyAttempted) return 'skip';
  if (args.bootstrap === 'pending') return 'defer';
  if (args.isStreaming || args.clientToolsBusy) return 'defer';
  return 'resume';
}

/**
 * Only a genuine local build failure justifies spending a prototype credit on an LLM fix turn.
 * `skipped` (watchdog, conversation switch, another tab won the race) and
 * `environment_failure` (the browser could not run the build) must never auto-spend, and a
 * turn that already had its automatic fix attempt must not get another one each time the
 * conversation is reopened.
 */
export function shouldStartFixStreamAfterResume(args: {
  outcome: ResumeBuildOutcome;
  hasPrototypeCredits: boolean;
  alreadyAttempted?: boolean;
}): boolean {
  return args.outcome === 'failed' && args.hasPrototypeCredits && args.alreadyAttempted !== true;
}
